import React, { useState } from "react";
import { Send, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import useAppNotice from "../common/useAppNotice";
import {
  applyCoachRpeToSession,
  closeRpeSession,
  deleteRpeSession,
  getRpeSessionMissingCount,
  isRpeSessionOpen,
} from "./rpeSessionApi.js";

const ERROR_KEYS = {
  coach_rpe_value_required: "rpe.coach_reference_required",
  invalid_coach_rpe_value: "rpe.coach_reference_required",
  session_closed: "rpe.error_close_session",
  error_close_session: "rpe.error_close_session",
  error_delete_session: "rpe.error_delete_session",
  error_quick_send: "rpe.error_quick_send",
};

const RpeSessionManageBar = ({
  session,
  clubId,
  isDarkMode = false,
  targetDate = null,
  onMutated,
  className = "",
}) => {
  const { t } = useTranslation();
  const { dialog: noticeDialog, alert: showAlert, confirm: showConfirm } = useAppNotice(isDarkMode);
  const [busyAction, setBusyAction] = useState(null);

  if (!session?.session_id) return noticeDialog;

  const sessionId = session.session_id;
  const isOpen = isRpeSessionOpen(session);
  const missingCount = getRpeSessionMissingCount(session);
  const coachValue = Number(session.coach_rpe_value);
  const hasCoachValue = Number.isFinite(coachValue) && coachValue >= 1 && coachValue <= 10;
  const busy = Boolean(busyAction);
  const showQuickSend = hasCoachValue && missingCount > 0;

  const notify = (type) => {
    onMutated?.({ type, sessionId });
  };

  const noticeError = (message) => showAlert({
    variant: "error",
    title: t("rpe.dialog_error_title"),
    message,
    confirmLabel: t("common.ok"),
  });

  const noticeWarning = (message) => showAlert({
    variant: "warning",
    title: t("rpe.dialog_coach_required_title"),
    message,
    confirmLabel: t("common.ok"),
  });

  const run = async (action, task) => {
    if (busy) return;
    setBusyAction(action);
    try {
      await task();
    } catch (error) {
      const key = ERROR_KEYS[error?.code];
      await noticeError(key ? t(key) : t("rpe.error_generic", { message: error?.message || String(error) }));
    } finally {
      setBusyAction(null);
    }
  };

  const handleClose = async () => {
    if (missingCount > 0 && !hasCoachValue) {
      await noticeWarning(t("rpe.coach_reference_required"));
      return;
    }
    const confirmed = await showConfirm({
      variant: "warning",
      title: t("rpe.dialog_close_title"),
      message: missingCount > 0
        ? t("rpe.confirm_close_partial_body")
        : t("rpe.confirm_close_session_body"),
      highlights: missingCount > 0
        ? [
            { label: t("rpe.dialog_highlight_missing"), value: String(missingCount) },
            { label: t("rpe.dialog_highlight_coach_rpe"), value: String(coachValue) },
          ]
        : undefined,
      confirmLabel: t("rpe.close_session"),
      cancelLabel: t("common.cancel"),
    });
    if (!confirmed) return;
    run("close", async () => {
      await closeRpeSession(sessionId, clubId, { targetDate });
      notify("closed");
    });
  };

  const handleQuickSend = async () => {
    if (!hasCoachValue) {
      await noticeWarning(t("rpe.coach_reference_required"));
      return;
    }
    const confirmed = await showConfirm({
      variant: "send",
      icon: "send",
      title: t("rpe.dialog_quick_send_title"),
      message: t(isOpen ? "rpe.confirm_quick_send_body" : "rpe.confirm_quick_send_remaining_body"),
      highlights: [
        { label: t("rpe.dialog_highlight_missing"), value: String(missingCount) },
        { label: t("rpe.dialog_highlight_coach_rpe"), value: String(coachValue) },
      ],
      confirmLabel: t("rpe.quick_send"),
      cancelLabel: t("common.cancel"),
    });
    if (!confirmed) return;
    run("quick", async () => {
      await applyCoachRpeToSession(sessionId, { close: isOpen, targetDate });
      notify("quick_send");
    });
  };

  const handleDelete = async () => {
    const confirmed = await showConfirm({
      variant: "danger",
      icon: "trash",
      title: t("rpe.dialog_delete_title"),
      message: t("rpe.confirm_delete_session_body"),
      confirmLabel: t("rpe.delete_session"),
      cancelLabel: t("common.cancel"),
    });
    if (!confirmed) return;
    run("delete", async () => {
      await deleteRpeSession(sessionId);
      notify("deleted");
    });
  };

  const buttonBase = `inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition disabled:opacity-50 disabled:cursor-not-allowed`;
  const secondaryClass = isDarkMode
    ? "border border-gray-600 bg-gray-800 text-gray-100 hover:bg-gray-700"
    : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50";

  return (
    <>
      <div className={`flex flex-wrap items-center gap-2 ${className}`}>
        {isOpen && (
          <button
            type="button"
            disabled={busy}
            onClick={handleClose}
            className={`${buttonBase} ${
              isDarkMode
                ? "bg-emerald-600 text-white hover:bg-emerald-500"
                : "bg-emerald-600 text-white hover:bg-emerald-700"
            }`}
          >
            {busyAction === "close" ? t("rpe.closing") : t("rpe.close_session")}
          </button>
        )}
        {showQuickSend && (
          <button
            type="button"
            disabled={busy}
            onClick={handleQuickSend}
            className={`${buttonBase} ${
              isDarkMode
                ? "bg-amber-500 text-gray-950 hover:bg-amber-400"
                : "bg-amber-500 text-white hover:bg-amber-600"
            }`}
          >
            <Send size={14} />
            {busyAction === "quick" ? t("rpe.quick_sending") : t("rpe.quick_send")}
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={handleDelete}
          className={`${buttonBase} ${secondaryClass} ${
            isDarkMode ? "text-red-300 hover:border-red-400" : "text-red-700 hover:border-red-300"
          }`}
        >
          <Trash2 size={14} />
          {busyAction === "delete" ? t("rpe.deleting_session") : t("rpe.delete_session")}
        </button>
      </div>
      {noticeDialog}
    </>
  );
};

export default RpeSessionManageBar;
